import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { storedFilesApi } from '../../api/storedFiles';
import type { StoredFile } from '../../api/storedFiles';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ConfigModal, type ConfigData } from '../../components/ui/ConfigModal';
import {
  HardDrive,
  Upload,
  Trash2,
  RefreshCw,
  Clock,
  FileCode,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Database,
  Zap,
  Check,
  X,
  AlertCircle,
  FileSymlink,
  Plus,
  Settings,
  FileJson,
} from 'lucide-react';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

function getDaysUntilExpiry(expiresAt: string): number {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function StoredFilesPage() {
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Selected files for upload
  const [selectedBinary, setSelectedBinary] = useState<File | null>(null);
  const [selectedPdb, setSelectedPdb] = useState<File | null>(null);
  
  // Config modal state
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedFileForConfig, setSelectedFileForConfig] = useState<StoredFile | null>(null);
  
  const binaryInputRef = useRef<HTMLInputElement>(null);
  const pdbInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadFiles();
  }, [page]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const response = await storedFilesApi.list(page, 10);
      setFiles(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Failed to load stored files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedBinary) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const result = await storedFilesApi.upload(selectedBinary, selectedPdb || undefined);
      const pdbInfo = result.hasPdb ? ` with PDB (${result.functionsCount} functions)` : '';
      setUploadSuccess(`File "${result.filename}" stored successfully${pdbInfo}!`);
      setSelectedBinary(null);
      setSelectedPdb(null);
      loadFiles(); // Refresh the list
    } catch (error: any) {
      setUploadError(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    
    for (const file of droppedFiles) {
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (['.exe', '.dll', '.sys'].includes(ext)) {
        setSelectedBinary(file);
      } else if (ext === '.pdb') {
        setSelectedPdb(file);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleBinarySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedBinary(file);
    }
    if (binaryInputRef.current) {
      binaryInputRef.current.value = '';
    }
  };

  const handlePdbSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedPdb(file);
    }
    if (pdbInputRef.current) {
      pdbInputRef.current.value = '';
    }
  };

  const clearSelection = () => {
    setSelectedBinary(null);
    setSelectedPdb(null);
  };

  const handleDelete = async (fileId: string) => {
    if (!confirm('Are you sure you want to delete this stored file? This cannot be undone.')) return;

    try {
      await storedFilesApi.delete(fileId);
      setFiles(files.filter((f) => f.id !== fileId));
    } catch (error) {
      console.error('Failed to delete stored file:', error);
    }
  };

  const handleExtendExpiry = async (fileId: string) => {
    try {
      const result = await storedFilesApi.extendExpiry(fileId);
      setFiles(files.map((f) =>
        f.id === fileId ? { ...f, expiresAt: result.expiresAt } : f
      ));
    } catch (error) {
      console.error('Failed to extend expiry:', error);
    }
  };

  const openConfigModal = (file: StoredFile) => {
    setSelectedFileForConfig(file);
    setConfigModalOpen(true);
  };

  const handleSaveConfig = async (config: ConfigData) => {
    if (!selectedFileForConfig) return;

    await storedFilesApi.saveSettings(selectedFileForConfig.id, {
      engine: config.savedEngine || undefined,
      engineVersion: config.savedEngineVersion || undefined,
      settings: config.savedSettings || undefined,
      functions: config.savedFunctions || undefined,
    });

    // Refresh the file list to get updated config
    await loadFiles();
    
    // Update the selected file for the modal
    const updatedFile = files.find(f => f.id === selectedFileForConfig.id);
    if (updatedFile) {
      setSelectedFileForConfig({
        ...updatedFile,
        savedEngine: config.savedEngine,
        savedEngineVersion: config.savedEngineVersion,
        savedSettings: config.savedSettings,
        savedFunctions: config.savedFunctions,
        hasConfig: !!(config.savedEngine || config.savedSettings),
      });
    }
  };

  const handleClearConfig = async () => {
    if (!selectedFileForConfig) return;

    await storedFilesApi.clearSettings(selectedFileForConfig.id);
    
    // Refresh the file list
    await loadFiles();
    
    // Update the selected file for the modal
    setSelectedFileForConfig({
      ...selectedFileForConfig,
      savedEngine: null,
      savedEngineVersion: null,
      savedSettings: null,
      savedFunctions: null,
      hasConfig: false,
    });
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 opacity-0-init animate-fade-in-up">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-2 font-mono">
              <HardDrive className="w-4 h-4 text-cyan-500" />
              <span>File Storage</span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">Stored Files</h1>
            <p className="text-slate-400 text-sm sm:text-base">
              Upload files once, reuse them for multiple builds without re-uploading
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button variant="ghost" onClick={loadFiles} className="flex-1 sm:flex-none">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Upload Area */}
        <Card className="opacity-0-init animate-fade-in-up animation-delay-100">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-cyan-400" />
              Upload New File
            </h3>

            {/* Success/Error Messages */}
            {uploadSuccess && (
              <div className="mb-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-3">
                <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-emerald-400 text-sm">{uploadSuccess}</span>
                <button
                  onClick={() => setUploadSuccess(null)}
                  className="ml-auto text-emerald-400/60 hover:text-emerald-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {uploadError && (
              <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-red-400 text-sm">{uploadError}</span>
                <button
                  onClick={() => setUploadError(null)}
                  className="ml-auto text-red-400/60 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Hidden file inputs */}
            <input
              ref={binaryInputRef}
              type="file"
              accept=".exe,.dll,.sys"
              onChange={handleBinarySelect}
              className="hidden"
            />
            <input
              ref={pdbInputRef}
              type="file"
              accept=".pdb"
              onChange={handlePdbSelect}
              className="hidden"
            />

            {uploading ? (
              <div className="border-2 border-dashed border-slate-700 rounded-2xl p-8 text-center">
                <div className="flex flex-col items-center">
                  <div className="relative w-12 h-12 mb-4">
                    <div className="absolute inset-0 rounded-full border-2 border-slate-800" />
                    <div className="absolute inset-0 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
                  </div>
                  <p className="text-slate-400">Uploading and analyzing...</p>
                </div>
              </div>
            ) : !selectedBinary ? (
              /* Drop Zone - when no file is selected */
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => binaryInputRef.current?.click()}
                className={`
                  relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer
                  transition-all duration-300
                  ${dragActive
                    ? 'border-cyan-500 bg-cyan-500/10'
                    : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800/30'
                  }
                `}
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-800/50 flex items-center justify-center border border-slate-700/50">
                  <Upload className={`w-8 h-8 ${dragActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                </div>
                <p className="text-white font-medium mb-2">
                  Drop your files here or click to browse
                </p>
                <p className="text-slate-500 text-sm mb-2">
                  Supports .exe, .dll, .sys files up to 100MB
                </p>
                <p className="text-purple-400 text-xs">
                  Tip: You can also drop a .pdb file at the same time for function analysis
                </p>
              </div>
            ) : (
              /* File Selection UI - when binary is selected */
              <div className="space-y-4">
                {/* Binary File Selected */}
                <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                        <FileCode className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{selectedBinary.name}</p>
                        <p className="text-slate-500 text-xs">{formatFileSize(selectedBinary.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedBinary(null)}
                      className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-red-400 transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* PDB File Selection */}
                {selectedPdb ? (
                  <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <FileSymlink className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">{selectedPdb.name}</p>
                          <p className="text-slate-500 text-xs">{formatFileSize(selectedPdb.size)} • PDB Debug Symbols</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedPdb(null)}
                        className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-red-400 transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => pdbInputRef.current?.click()}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-slate-700 hover:border-purple-500/50 hover:bg-purple-500/5 transition-all flex items-center justify-center gap-3 group"
                  >
                    <Plus className="w-5 h-5 text-slate-500 group-hover:text-purple-400" />
                    <span className="text-slate-500 group-hover:text-purple-400 text-sm">
                      Add PDB file (optional) for function-level protection
                    </span>
                  </button>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button variant="ghost" onClick={clearSelection} className="flex-1">
                    Cancel
                  </Button>
                  <Button glow onClick={handleUpload} className="flex-1">
                    <Upload className="w-4 h-4" />
                    Store File{selectedPdb ? 's' : ''}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Files List */}
        <Card className="opacity-0-init animate-fade-in-up animation-delay-200">
          <div className="p-4 border-b border-slate-800/50">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-400" />
              Your Stored Files
              {files.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-mono bg-purple-500/20 text-purple-400 rounded-lg border border-purple-500/20">
                  {files.length}
                </span>
              )}
            </h3>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative w-12 h-12 mb-4">
                <div className="absolute inset-0 rounded-full border-2 border-slate-800" />
                <div className="absolute inset-0 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
              </div>
              <p className="text-slate-500 font-mono text-sm">Loading files...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-800/50 flex items-center justify-center border border-slate-700/50">
                <HardDrive className="w-10 h-10 text-slate-600" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No stored files yet</h3>
              <p className="text-slate-500 mb-6">Upload a file above to store it for reuse</p>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="lg:hidden p-4 space-y-3">
                {files.map((file, index) => {
                  const daysLeft = getDaysUntilExpiry(file.expiresAt);
                  return (
                    <div
                      key={file.id}
                      className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 space-y-3 opacity-0-init animate-fade-in-up"
                      style={{ animationDelay: `${0.2 + index * 0.05}s` }}
                    >
                      {/* Header with filename */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <FileCode className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                            <p className="font-mono text-white text-sm truncate">{file.filename}</p>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                            <span>{formatFileSize(file.size)}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-600" />
                            <span>{file.buildsCount} builds</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {file.hasConfig && (
                            <span className="px-2 py-0.5 text-xs font-mono bg-emerald-500/20 text-emerald-400 rounded-lg border border-emerald-500/20 flex items-center gap-1">
                              <Settings className="w-3 h-3" />
                              Config
                            </span>
                          )}
                          {file.hasPdb && (
                            <span className="px-2 py-0.5 text-xs font-mono bg-purple-500/20 text-purple-400 rounded-lg border border-purple-500/20">
                              PDB
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Config info */}
                      {file.hasConfig && file.savedEngine && (
                        <div className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                          <div className="flex items-center gap-2 text-xs">
                            <Settings className="w-3 h-3 text-emerald-400" />
                            <span className="text-emerald-400 font-medium">{file.savedEngine}</span>
                            {file.savedEngineVersion && (
                              <span className="text-slate-500">v{file.savedEngineVersion}</span>
                            )}
                            {file.savedFunctions && file.savedFunctions.length > 0 && (
                              <span className="text-slate-500">• {file.savedFunctions.length} funcs</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Expiry info */}
                      <div className="flex items-center gap-2">
                        <Clock className={`w-3.5 h-3.5 ${daysLeft <= 7 ? 'text-amber-400' : 'text-slate-500'}`} />
                        <span className={`text-xs ${daysLeft <= 7 ? 'text-amber-400' : 'text-slate-500'}`}>
                          {daysLeft <= 0 ? 'Expired' : `Expires in ${daysLeft} days`}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                        <span className="text-slate-500 text-xs font-mono">
                          {formatDate(file.createdAt)}
                        </span>
                        <div className="flex items-center gap-1">
                          <Link to={`/builds/new?storedFileId=${file.id}`}>
                            <Button size="sm" className="h-8 px-3">
                              <Zap className="w-3.5 h-3.5" />
                              Build
                            </Button>
                          </Link>
                          <button
                            onClick={() => openConfigModal(file)}
                            className={`p-2 rounded-lg transition-all ${
                              file.hasConfig
                                ? 'text-emerald-400 hover:bg-emerald-500/10'
                                : 'text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10'
                            }`}
                            title="Manage config"
                          >
                            <FileJson className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleExtendExpiry(file.id)}
                            className="p-2 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-all"
                            title="Extend expiry"
                          >
                            <Calendar className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(file.id)}
                            className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800/50">
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Filename</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Size</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Builds</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">PDB</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Config</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Created</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Expires</th>
                      <th className="text-right py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file, index) => {
                      const daysLeft = getDaysUntilExpiry(file.expiresAt);
                      return (
                        <tr
                          key={file.id}
                          className="border-b border-slate-800/30 hover:bg-slate-800/30 transition-colors group opacity-0-init animate-fade-in-up"
                          style={{ animationDelay: `${0.2 + index * 0.05}s` }}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <FileCode className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                              <span className="font-mono text-white group-hover:text-cyan-400 transition-colors truncate max-w-[200px]">
                                {file.filename}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-slate-400 text-sm font-mono">
                            {formatFileSize(file.size)}
                          </td>
                          <td className="py-4 px-4">
                            <span className="px-2 py-0.5 text-xs font-mono bg-slate-700/50 text-slate-300 rounded-lg">
                              {file.buildsCount}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            {file.hasPdb ? (
                              <span className="px-2 py-0.5 text-xs font-mono bg-purple-500/20 text-purple-400 rounded-lg border border-purple-500/20">
                                Yes ({file.functionsCount || 0} funcs)
                              </span>
                            ) : (
                              <span className="text-slate-600">No</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {file.hasConfig ? (
                              <div className="flex flex-col gap-1">
                                <span className="px-2 py-0.5 text-xs font-mono bg-emerald-500/20 text-emerald-400 rounded-lg border border-emerald-500/20 inline-flex items-center gap-1 w-fit">
                                  <Settings className="w-3 h-3" />
                                  {file.savedEngine || 'Custom'}
                                </span>
                                {file.savedFunctions && file.savedFunctions.length > 0 && (
                                  <span className="text-slate-500 text-xs">
                                    {file.savedFunctions.length} funcs selected
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-600">None</span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-slate-500 text-sm font-mono">
                            {formatDate(file.createdAt)}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`text-sm font-mono ${daysLeft <= 7 ? 'text-amber-400' : 'text-slate-500'}`}>
                              {daysLeft <= 0 ? 'Expired' : `${daysLeft} days`}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center justify-end gap-1">
                              <Link to={`/builds/new?storedFileId=${file.id}`}>
                                <Button size="sm" className="h-8 px-3">
                                  <Zap className="w-3.5 h-3.5" />
                                  Build
                                </Button>
                              </Link>
                              <button
                                onClick={() => openConfigModal(file)}
                                className={`p-2 rounded-lg transition-all hover:scale-110 ${
                                  file.hasConfig
                                    ? 'text-emerald-400 hover:bg-emerald-500/10'
                                    : 'text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10'
                                }`}
                                title="Manage config"
                              >
                                <FileJson className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleExtendExpiry(file.id)}
                                className="p-2 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-all hover:scale-110"
                                title="Extend expiry by 30 days"
                              >
                                <Calendar className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(file.id)}
                                className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all hover:scale-110"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 p-4 border-t border-slate-800/50">
                  <div className="flex items-center gap-2 order-2 sm:order-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="px-2 sm:px-3"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Previous</span>
                    </Button>

                    <div className="flex items-center gap-1 sm:gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => {
                          if (totalPages <= 5) return true;
                          return p === 1 || p === totalPages || Math.abs(p - page) <= 1;
                        })
                        .map((p, idx, arr) => (
                          <React.Fragment key={p}>
                            {idx > 0 && arr[idx - 1] !== p - 1 && (
                              <span className="text-slate-500 px-1">...</span>
                            )}
                            <button
                              onClick={() => setPage(p)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                                p === page
                                  ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white'
                                  : 'text-slate-500 hover:text-white hover:bg-slate-800'
                              }`}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="px-2 sm:px-3"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="text-xs text-slate-500 font-mono order-1 sm:order-2 sm:hidden">
                    Page {page} of {totalPages}
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Config Modal */}
      {selectedFileForConfig && (
        <ConfigModal
          isOpen={configModalOpen}
          onClose={() => {
            setConfigModalOpen(false);
            setSelectedFileForConfig(null);
          }}
          filename={selectedFileForConfig.filename}
          config={{
            savedEngine: selectedFileForConfig.savedEngine ?? null,
            savedEngineVersion: selectedFileForConfig.savedEngineVersion ?? null,
            savedSettings: selectedFileForConfig.savedSettings ?? null,
            savedFunctions: selectedFileForConfig.savedFunctions ?? null,
          }}
          onSave={handleSaveConfig}
          onClear={handleClearConfig}
        />
      )}
    </Layout>
  );
}

